import {Button, FormControl, FormField, openDialog, Separator} from 'platform/components';
import {Box, Heading, HStack, Space, Text, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {useGetServiceOrderGroupQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';
import {AllServiceOrderGroups} from './components/AllServiceOrderGroups';

interface ReportingTabProps extends RequiredTestIdProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
}

export function ReportingTab(props: ReportingTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const {data: orderGroups} = useGetServiceOrderGroupQuery();

  const orderGroupsOptions = orderGroups?.map((group) => ({
    label: group?.name,
    value: group?.serviceOrderGroupId,
  }));

  const handleViewAll = () =>
    openDialog(
      <AllServiceOrderGroups data-testid={suffixTestId('allServiceOrderGroups', props)} />,
      {
        title: i18n.t('entity.accounting.lables.allServiceOrderGroups'),
      }
    );

  const excludedServiceOrderGroupIds = props.formApi.watch(
    'reporting.excludedServiceOrderGroupIds'
  );

  const filteredOrderGroupsOptions =
    orderGroupsOptions?.filter(
      (option) => !excludedServiceOrderGroupIds?.includes(option.value ?? '')
    ) ?? [];

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('entity.accounting.labels.serviceOrderGroups')}</Heading>
            <Button
              title={i18n.t('general.actions.viewAll')}
              variant="link"
              leftIcon="action/visibility"
              onClick={handleViewAll}
              data-testid={suffixTestId('viewAll', props)}
            />
          </HStack>
          <FormField
            control={props.control}
            label={i18n.t('entity.accounting.labels.excludedServiceOrderGroups')}
            name="reporting.excludedServiceOrderGroupIds"
            options={orderGroupsOptions ?? []}
            type="multiChoice"
            data-testid={suffixTestId('excludedServiceOrderGroups', props)}
          />
          <HStack spacing={4} align="center">
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.accounting.labels.defaultServiceOrderGroup')}
                name="reporting.defaultServiceOrderGroupId"
                options={filteredOrderGroupsOptions}
                type="choice"
                data-testid={suffixTestId('defaultServiceOrderGroup', props)}
              />
            </Box>
            <Box flex={1}>
              <VStack>
                <Space vertical={5} />
                <FormField
                  control={props.control}
                  type="checkbox"
                  label={i18n.t('entity.accounting.labels.lockDefault')}
                  name="reporting.isDefaultServiceOrderGroupLocked"
                  data-testid={suffixTestId('lockDefaultServiceCenter', props)}
                />
              </VStack>
            </Box>
          </HStack>
        </VStack>
        <Separator />
        <VStack spacing={4}>
          <Text color="secondary" size="small">
            {i18n.t('entity.order.labels.isPreDeliveryInspectionDescription')}
          </Text>
          <FormField
            control={props.control}
            type="checkbox"
            label={i18n.t('entity.order.labels.isPreDeliveryInspection')}
            name="reporting.isPreDeliveryInspection"
          />
        </VStack>
      </SettingsSection>
      <Space vertical={20} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={suffixTestId('footer', props)}
      />
    </>
  );
}
